/**
 * OPFS I/O Worker Service — Transparent worker offload with main-thread fallback
 * 
 * Wraps OPFS read/write/delete operations behind a unified async API.
 * If a Web Worker is available, I/O uses createSyncAccessHandle (faster, non-blocking).
 * If the worker fails to load, falls back to the existing main-thread async OPFS
 * via getFileFromOPFS / saveFileToOPFS — zero behavior change, same results.
 * 
 * Primary consumer: MerkleTreeService progress flush loop (~53KB writes every 5s)
 * 
 * Usage:
 *   import { opfsIOWorkerService } from "@/services/opfs-io-worker-service";
 *   await opfsIOWorkerService.init();
 *   await opfsIOWorkerService.write("merkle_tree_abc123", serializedData);
 *   const data = await opfsIOWorkerService.read("merkle_tree_abc123");
 */

import { getFileFromOPFS, saveFileToOPFS, saveFileToOPFSAsync, sanitizeOPFSFilename } from '@/lib/file';

// ============================================================
// Types
// ============================================================

type PendingRequest = {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
  /**
   * P0-D: Progress callback for long-running streamed writes
   * (`writeFileStreamed`).  The worker posts zero or more
   * `{ id, kind: 'progress', progress }` messages before the
   * final completion message.  Progress messages do NOT
   * delete the pending-request entry; the entry is deleted
   * on completion (ok / error) only.
   */
  onProgress?: (percent: number) => void;
};

// ============================================================
// Service Implementation
// ============================================================

class OPFSIOWorkerService {
  private worker: Worker | null = null;
  private workerAvailable = false;
  private pendingRequests = new Map<number, PendingRequest>();
  private nextRequestId = 0;
  private initPromise: Promise<void> | null = null;
  // P0-D follow-up: one-shot diagnostic so the silent main-thread fallback
  // path logs exactly once per session (see writeFileStreamed).
  private _loggedFallbackReason = false;

  // ============================================================
  // Initialization
  // ============================================================

  /**
   * Initialize the service. Safe to call multiple times — subsequent calls
   * return the same promise. Spawns the worker.
   */
  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this._doInit();
    return this.initPromise;
  }

  private async _doInit(): Promise<void> {
    try {
      await this.spawnWorker();
    } catch (e) {
      console.warn('[OPFSIOWorkerService] Worker spawn failed, using main-thread fallback', e);
    }

    console.log(`[OPFSIOWorkerService] Initialized — worker: ${this.workerAvailable ? 'YES (sync access handle)' : 'NO (main-thread async fallback)'}`);
  }

  private spawnWorker(): Promise<void> {
    if (typeof Worker === 'undefined') {
      return Promise.reject(new Error('Web Workers not supported'));
    }

    return new Promise<void>((resolve, reject) => {
      // P0-I Phase 1S root-cause fix: the previous 5 s init timeout was
      // too aggressive on resource-constrained peers (Kali Firefox on
      // Pi4-class hardware).  The worker itself spawns fine, but its
      // init-response is queued behind the cold-start activity burst
      // (peer connection, ICE gathering, Dexie subscriptions, WASM
      // init).  When the timeout fired we silently fell back to
      // main-thread async OPFS — which during a merkle-tree progress
      // flush loop lands ~53 KB writes on the main thread every 5 s,
      // saturating the scheduler and starving React's Suspense retry
      // for minutes (254 s observed 4/26).  30 s timeout + 5 s progress
      // logging lets us distinguish "slow init that still worked" from
      // "genuine worker failure" without giving up early.
      const INIT_TIMEOUT_MS = 30000;
      const start = performance.now();
      const progressInterval = setInterval(() => {
        const elapsed = Math.round((performance.now() - start) / 1000);
        console.warn(
          `[OPFSIOWorkerService] Worker init still pending after ${elapsed}s (will timeout at 30s) — main thread likely saturated`,
        );
      }, 5000);
      const timeout = setTimeout(() => {
        clearInterval(progressInterval);
        reject(new Error(`Worker init timed out after ${INIT_TIMEOUT_MS / 1000}s`));
      }, INIT_TIMEOUT_MS);

      try {
        // Worker is built as a separate entry point by build.ts → /opfs-io-worker.js
        this.worker = new Worker('/opfs-io-worker.js', { type: 'module' });

        this.worker.onmessage = (e: MessageEvent) => {
          this.handleMessage(e);
        };

        this.worker.onerror = (e: ErrorEvent) => {
          console.warn('[OPFSIOWorkerService] Worker error:', e.message);
          clearInterval(progressInterval);
          clearTimeout(timeout);
          this.teardownWorker();
          reject(new Error(e.message));
        };

        // Send init command and wait for OPFS ready
        const initId = this.nextRequestId++;
        this.pendingRequests.set(initId, {
          resolve: () => {
            clearInterval(progressInterval);
            clearTimeout(timeout);
            this.workerAvailable = true;
            const elapsed = Math.round((performance.now() - start) / 1000);
            if (elapsed >= 5) {
              console.log(
                `[OPFSIOWorkerService] Worker init completed after ${elapsed}s (slow but successful)`,
              );
            }
            resolve();
          },
          reject: (err) => {
            clearInterval(progressInterval);
            clearTimeout(timeout);
            this.teardownWorker();
            reject(err);
          }
        });

        this.worker.postMessage({ task: 'init', id: initId });
      } catch (e) {
        clearInterval(progressInterval);
        clearTimeout(timeout);
        reject(e);
      }
    });
  }

  private teardownWorker(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.workerAvailable = false;
    for (const [, pending] of this.pendingRequests) {
      pending.reject(new Error('Worker terminated'));
    }
    this.pendingRequests.clear();
  }

  // ============================================================
  // Worker Communication
  // ============================================================

  private handleMessage(e: MessageEvent): void {
    const { id, error, kind, ...result } = e.data;
    const pending = this.pendingRequests.get(id);
    if (!pending) return;

    // P0-D: Progress event for writeFileStreamed.  Notify callback but leave
    // the pending-request entry intact so the eventual completion message
    // can still resolve it.
    if (kind === 'progress' && !error) {
      try {
        pending.onProgress?.(typeof result.progress === 'number' ? result.progress : 0);
      } catch (cbErr) {
        console.warn('[OPFSIOWorkerService] onProgress callback threw', cbErr);
      }
      return;
    }

    this.pendingRequests.delete(id);

    if (error) {
      pending.reject(new Error(error));
    } else {
      pending.resolve(result);
    }
  }

  private sendToWorker(task: string, data: Record<string, any>, transferables?: Transferable[]): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.worker || !this.workerAvailable) {
        reject(new Error('Worker not available'));
        return;
      }

      const id = this.nextRequestId++;
      this.pendingRequests.set(id, { resolve, reject });

      const message = { task, id, ...data };
      this.worker.postMessage(message, transferables || []);
    });
  }

  // ============================================================
  // Public API
  // ============================================================

  /**
   * Write binary data to OPFS.
   * Routes to worker (sync access handle) if available, otherwise main-thread async.
   * 
   * @param key - OPFS filename (will be sanitized)
   * @param data - Binary data to write
   * @returns bytes written
   */
  async write(key: string, data: Uint8Array): Promise<number> {
    if (this.workerAvailable) {
      try {
        const result = await this.sendToWorker('write', { key, data }, [data.buffer]);
        return result.bytesWritten as number;
      } catch (e) {
        console.warn('[OPFSIOWorkerService] Worker write failed, falling back to main thread', e);
        // Fall through to main-thread path
      }
    }

    // Main-thread fallback — existing async OPFS
    // OPFS_FIX: Retry once on InvalidStateError (stale handles from memory pressure)
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const blob = new Blob([new Uint8Array(data)], { type: 'application/octet-stream' });
        const file = new File([blob], key, { type: 'application/octet-stream' });
        await saveFileToOPFS(file, key);
        return data.byteLength;
      } catch (err: any) {
        if (attempt === 0 && err?.name === 'InvalidStateError') {
          console.warn('[OPFSIOWorkerService] Main-thread write InvalidStateError, retrying...', err);
          await new Promise(r => setTimeout(r, 200));
          continue;
        }
        throw err;
      }
    }
    return data.byteLength;
  }

  /**
   * Read binary data from OPFS.
   * Routes to worker (sync access handle) if available, otherwise main-thread async.
   * 
   * @param key - OPFS filename (will be sanitized)
   * @returns Binary data or null if not found
   */
  async read(key: string): Promise<Uint8Array | null> {
    if (this.workerAvailable) {
      try {
        const result = await this.sendToWorker('read', { key });
        return result.data as Uint8Array | null;
      } catch (e) {
        console.warn('[OPFSIOWorkerService] Worker read failed, falling back to main thread', e);
        // Fall through to main-thread path
      }
    }

    // Main-thread fallback — existing async OPFS
    // OPFS_FIX: Retry once on InvalidStateError (stale handles from memory pressure)
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const file = await getFileFromOPFS(key);
        if (!file) return null;
        const buffer = await file.arrayBuffer();
        return new Uint8Array(buffer);
      } catch (err: any) {
        if (attempt === 0 && err?.name === 'InvalidStateError') {
          console.warn('[OPFSIOWorkerService] Main-thread read InvalidStateError, retrying...', err);
          await new Promise(r => setTimeout(r, 200));
          continue;
        }
        throw err;
      }
    }
    return null;
  }

  /**
   * Delete a file from OPFS.
   * Routes to worker if available, otherwise main-thread async.
   * 
   * @param key - OPFS filename (will be sanitized)
   */
  async delete(key: string): Promise<void> {
    if (this.workerAvailable) {
      try {
        await this.sendToWorker('delete', { key });
        return;
      } catch (e) {
        console.warn('[OPFSIOWorkerService] Worker delete failed, falling back to main thread', e);
      }
    }

    // Main-thread fallback — best effort
    try {
      const root = await navigator.storage.getDirectory();
      await root.removeEntry(key);
    } catch {
      // Silently ignore — file may not exist
    }
  }

  /**
   * P0-D: Write a large File/Blob to OPFS by streaming it through the
   * worker, chunk by chunk, using `createSyncAccessHandle` on the worker
   * thread.  This is the recommended path for any file >16 MiB — on
   * Firefox it is 10-50× faster than the main-thread async path AND
   * (critically) does not block the event loop, so ICE consent refresh,
   * SCTP timers, and DTLS heartbeats continue to fire normally during
   * multi-GB writes.
   *
   * Fallback: if the worker is unavailable (Safari iOS without sync-access
   * support, worker init failed, or a previous worker task crashed), the
   * call transparently degrades to the main-thread `saveFileToOPFSAsync`
   * path.  Behavior and observable result are identical, only speed and
   * ICE-stability differ.
   *
   * @param key     OPFS filename (will be sanitized).  Safe to pass the
   *                already-sanitized path from `sanitizeOPFSFilename` —
   *                sanitization is idempotent.
   * @param file    File or Blob to write.  Blob is structured-cloned to
   *                the worker by postMessage; the underlying bytes are
   *                NOT copied (browsers share the blob backing store
   *                across threads by reference).
   * @param chunkSize  Per-write chunk size in bytes.  Clamped to
   *                   [1 MiB, 64 MiB] inside the worker.  Defaults to
   *                   32 MiB if omitted.
   * @param onProgress Optional callback invoked with percentage complete
   *                   (0..100).  Posted every ~5% or 64 MiB, whichever
   *                   fires first.
   * @returns The total bytes written.
   */
  async writeFileStreamed(
    key: string,
    file: File | Blob,
    chunkSize?: number,
    onProgress?: (percent: number) => void
  ): Promise<number> {
    if (this.workerAvailable && this.worker) {
      try {
        const result = await this._writeFileStreamedViaWorker(key, file, chunkSize, onProgress);
        return result;
      } catch (e) {
        console.warn('[OPFSIOWorkerService] writeFileStreamed worker path failed, falling back to main thread', e);
        // Fall through to main-thread path
      }
    } else if (!this._loggedFallbackReason) {
      // First-time diagnostic: make silent fallback observable.  Without this,
      // a service that was never init'd would route every write through
      // saveFileToOPFSAsync with zero log evidence that the worker path was
      // skipped — exactly the failure mode that let the P0-D init bug hide.
      this._loggedFallbackReason = true;
      console.warn(
        `[OPFSIOWorkerService] writeFileStreamed using main-thread fallback — ` +
        `workerAvailable=${this.workerAvailable}, worker=${this.worker ? 'present' : 'null'}, ` +
        `initPromiseSettled=${this.initPromise ? 'yes' : 'never-called'}. ` +
        `If this is unexpected, confirm opfsIOWorkerService.init() runs at mount.`
      );
    }

    // Main-thread fallback: existing saveFileToOPFSAsync.  We still get the
    // speed benefit of per-chunk yields, just not the non-blocking benefit
    // of the worker.  On Firefox this will still stall the main thread for
    // multi-GB files — but only if the worker couldn't be used at all.
    const safePath = sanitizeOPFSFilename(key);
    const root = await navigator.storage.getDirectory();
    const handle = await root.getFileHandle(safePath, { create: true });
    await saveFileToOPFSAsync(
      handle,
      file as File,
      chunkSize,
      onProgress
    );
    return file.size;
  }

  /**
   * Internal worker-path implementation for writeFileStreamed.  Registers a
   * pending-request entry with an `onProgress` callback so streaming
   * progress messages are routed correctly by `handleMessage`.
   */
  private _writeFileStreamedViaWorker(
    key: string,
    file: File | Blob,
    chunkSize: number | undefined,
    onProgress: ((percent: number) => void) | undefined
  ): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      if (!this.worker || !this.workerAvailable) {
        reject(new Error('Worker not available'));
        return;
      }

      const id = this.nextRequestId++;
      this.pendingRequests.set(id, {
        resolve: (result: any) => {
          const bytesWritten = typeof result?.bytesWritten === 'number'
            ? result.bytesWritten
            : file.size;
          resolve(bytesWritten);
        },
        reject,
        onProgress,
      });

      // File/Blob are structured-cloneable but NOT transferable.  Browsers
      // share the blob's backing store across workers by reference-count,
      // so passing a 33 GB File via postMessage does NOT copy 33 GB of
      // bytes — only the handle wrapper is cloned.
      this.worker.postMessage({
        task: 'writeFileStreamed',
        id,
        key,
        file,
        chunkSize,
      });
    });
  }

  // ============================================================
  // Status
  // ============================================================

  /** Whether the worker is active and using sync access handles */
  get isWorkerActive(): boolean {
    return this.workerAvailable;
  }

  /** Tear down the worker. Call on unmount if needed. */
  destroy(): void {
    this.teardownWorker();
    this.initPromise = null;
  }
}

// ============================================================
// Singleton Export
// ============================================================

export const opfsIOWorkerService = new OPFSIOWorkerService();
